import moment from 'moment';
import numeral from 'numeral';
import theme from 'styles/theme';
import path from 'path-browserify';
import * as download from 'downloadjs';
import {
  split,
  chain,
  orderBy,
  isEmpty,
  toLower,
  includes,
  isString,
  startCase,
  startsWith,
} from 'lodash';

import { toastError } from './toast';

import { RiskSeverity } from './enum/risk';
import { RiskScore } from './enum/risk-score';

export const internalSuffix = '-private';
export const encryptedFieldPlaceholder = '*******************';

export const getRiskColor = (score) => {
  if (!score || score <= RiskScore[RiskSeverity.LOW])
    return theme.colors.severity0;
  if (score <= RiskScore[RiskSeverity.MEDIUM]) return theme.colors.severity2;
  if (score <= RiskScore[RiskSeverity.HIGH]) return theme.colors.severity3;

  return theme.colors.severity4;
};

export const getRiskLevel = (riskLevel) => {
  switch (true) {
    case riskLevel <= RiskScore[RiskSeverity.LOW]:
      return RiskSeverity.LOW;
    case riskLevel <= RiskScore[RiskSeverity.MEDIUM]:
      return RiskSeverity.MEDIUM;
    case riskLevel <= RiskScore[RiskSeverity.HIGH]:
      return RiskSeverity.HIGH;
    default:
      return RiskSeverity.CRITICAL;
  }
};

export const getRiskText = (risk) => {
  if (risk <= RiskScore[RiskSeverity.LOW]) return RiskSeverity.LOW;
  if (risk <= RiskScore[RiskSeverity.MEDIUM]) return RiskSeverity.MEDIUM;
  if (risk <= RiskScore[RiskSeverity.HIGH]) return RiskSeverity.HIGH;

  return RiskSeverity.CRITICAL;
};

export const sortTable = (sortBy, sortDirection, tableData) => {
  if (sortBy === 'remediation') {
    return orderBy(
      tableData,
      [
        (tableRow) =>
          !tableRow[sortBy] || tableRow[sortBy] === emptyStateAsString,
      ],
      sortDirection,
    );
  }

  return orderBy(
    tableData,
    [
      (tableRow) => {
        if (tableRow[sortBy] === '') return null;

        return typeof tableRow[sortBy] === 'string'
          ? tableRow[sortBy].toLowerCase()
          : tableRow[sortBy];
      },
    ],
    sortDirection,
  );
};

export const dateFormatType = {
  dateAndHour: {
    dateStyle: 'short',
    timeStyle: 'short',
    hourCycle: 'h23',
    type: 'dateAndHour',
  },
  dateOnly: {
    dateStyle: 'short',
    timeStyle: undefined,
    hourCycle: 'h23',
    type: 'dateOnly',
  },
  dateOnlyDotSeperated: {
    dateStyle: 'short',
    timeStyle: undefined,
    hourCycle: 'h23',
    type: 'dateOnlyDotSeperated',
  },
  dateAndMonth: {
    dateStyle: 'medium',
    timeStyle: undefined,
    hourCycle: 'h23',
    type: 'dateAndMonth',
  },
  dateMonthHour: {
    dateStyle: 'long',
    timeStyle: 'medium',
    hourCycle: 'h23',
    type: 'dateMonthHour',
  },
  dateMonthHourShort: {
    hourCycle: 'h23',
    dateStyle: 'short',
    timeStyle: 'short',
    type: 'dateMonthHourShort',
  },
  hourShort: {
    dateStyle: undefined,
    timeStyle: 'short',
    hourCycle: 'h12',
    type: 'hourShort',
  },
  fullDateDashSeperated: {
    type: 'fullDateDashSeperated',
  },
};

export const defaultDate = '1970-01-01T00:00:00';
export const indefinitelyDate = '3000-01-01T00:00:00';

export const isDefaultDate = (date) => moment(defaultDate).isSame(date);

export const isFutureDate = (date) => moment().utc().diff(moment.utc(date)) < 0;

export const isDateExpired = (date) => moment(date).isBefore(moment());

export const formatTimeToHour = (time) => moment(time).format('HH:mm');

export const formatDate = (
  date,
  format = dateFormatType.dateOnly,
  disableFuture = false,
) => {
  try {
    if (!date || isDefaultDate(date) || (disableFuture && isFutureDate(date)))
      return 'N/A';

    const dateToLocal = isValidUnixTimestamp(date)
      ? convertUtcToDate(date)
      : convertDateToLocalDate(date);

    const formattedDate = new Intl.DateTimeFormat(
      navigator.language,
      format,
    ).format(dateToLocal);

    if (format.type === dateFormatType.dateOnlyDotSeperated.type) {
      return formattedDate.replaceAll('/', '.');
    }
    if (format.type === dateFormatType.fullDateDashSeperated.type) {
      return moment().format('YYYY-MM-DD HH-mm-ss');
    }

    return formattedDate;
  } catch (error) {
    return 'invalid date';
  }
};

const convertDateToLocalDate = (date) => {
  date = new Date(date);
  const localTime = date.getTime();
  const localOffset = date.getTimezoneOffset() * 60000;

  return localTime - localOffset;
};

const convertUtcToDate = (date) => {
  date = new Date(date);
  const localTime = date.getTime();

  return localTime;
};

export const validateValueIsANumber = (value) =>
  typeof value === 'number' && !isNaN(value);

export const downloadFile = ({ fileName, fileData, mimeType }) => {
  try {
    download(fileData, path.basename(fileName), mimeType);
  } catch (error) {
    toastError('Failed downloading file', error);
  }
};

export const assignNullIfEmpty = (object) => {
  if (object && Object.keys(object).length === 0) {
    return null;
  }

  return object;
};

export const largeNumbersFormatter = Intl.NumberFormat(navigator.language, {
  maximumFractionDigits: 0,
  minimumFractionDigits: 0,
  notation: 'compact',
});

export const toTitleCase = (title) => startCase(toLower(title));

export const openUrl = (url) => {
  if (startsWith(url, 'http')) {
    return window.open(url);
  }

  return window.open(`http://${url}`);
};

export const formatUtcToDate = (value) =>
  value ? moment.utc(value).local() : null;

export const formatDateToUtc = (value) => moment.utc(value).local().valueOf();

export const formatNum = (number) => numeral(number).format('0,0');

export const isValidUnixTimestamp = (inputNumber) => {
  if (isNaN(inputNumber)) {
    return false;
  }

  const date = moment.unix(inputNumber);

  if (date.isValid() && date.year() > 2010) {
    return true;
  }

  return false;
};

export const isEmail = (email) => {
  if (!isString(email) || isEmpty(email)) {
    return false;
  }

  const [localPart, domainPart] = email.split('@');
  const emailPattern =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+\.[a-zA-Z]{2,}$/;

  if (
    !localPart ||
    !domainPart ||
    localPart.length > 64 ||
    domainPart.length > 255 ||
    email.length > 254
  ) {
    return false;
  }

  return emailPattern.test(email);
};

export const formatSourceTitle = ({
  displayName,
  applicationName,
  alternativeName = undefined,
}) => {
  const isNamesEqual = applicationName === (alternativeName || displayName);
  const title = isNamesEqual
    ? applicationName
    : `${applicationName} (${alternativeName || displayName})`;

  return title;
};

export const isJson = (str) => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }

  return true;
};

export const textToLinkableHtml = (text) =>
  chain(text)
    .replace(
      /(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gi,
      (match) => `<a href="${match}" target="_blank">${match}</a>`,
    )
    .split(/(?<=^|\n|\.\s)(?=\d{1,2}(?:\.\d+)*\.\s)/m)
    .map((line) => `<div>${line}</div>`)
    .join(' ')
    .valueOf();

export const sizeConvertor = (bytes, decimals = 2) => {
  if (!bytes) return;

  if (bytes === 0 || bytes === '0' || bytes < 1) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  let i = Math.floor(Math.log(bytes) / Math.log(k));

  if (i > sizes.length - 1) {
    i = sizes.length - 1;
  }

  if (decimals === 0) {
    return `${parseInt((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
  }

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

function importAll(required) {
  const images = {};

  for (const reqItem of required) {
    reqItem.keys().map((item) => {
      images[item.replace('./', '')] = reqItem(item);

      return null;
    });
  }

  return images;
}

export const images = importAll([
  require.context('assets/png/', false, /\.(png)$/),
  require.context('assets/jpg/blurred-images/', false, /\.(jpe?g)$/),
  require.context('assets/svg/forticloud-footer/', false, /\.(png|jpe?g|svg)$/),
  require.context(
    'assets/svg/applications-icons/small/',
    false,
    /\.(png|jpe?g|svg)$/,
  ),
  require.context(
    'assets/svg/applications-icons/large/',
    false,
    /\.(png|jpe?g|svg)$/,
  ),
]);

export const extractDomain = (url) => {
  let hostname;

  if (!url) return;

  try {
    hostname = new URL(url).hostname;
  } catch (error) {
    hostname = new URL(`http://${url}`).hostname;
  }
  const parts = split(hostname, '.');
  const tlds = ['co', 'org', 'net', 'com'];

  if (parts.length > 2) {
    let sliceIndex = -2;

    if (tlds.indexOf(parts[parts.length - 2]) !== -1) {
      sliceIndex = -3;
    }

    return chain(parts).slice(sliceIndex).join('.').valueOf();
  }

  return hostname;
};

const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });

export const getCountryName = (countryCode) => {
  try {
    return displayNames.of(countryCode);
  } catch (error) {
    return countryCode;
  }
};

const riskyCountries = [
  'Iran',
  'Cuba',
  'Burma',
  'Syria',
  'China',
  'Russia',
  'Eritrea',
  'Algeria',
  'Vietnam',
  'Comoros',
  'Pakistan',
  'Nicaragua',
  'Venezuela',
  'Azerbaijan',
  'Tajikistan',
  'North Korea',
  'Saudi Arabia',
  'Turkmenistan',
  'Central African Republic',
];

export const isCountryRisky = (country) => includes(riskyCountries, country);

export const subtractHours = ({ date, hours = 24 }) => {
  const currentDate = moment(date);
  const updatedDate = currentDate.subtract(hours, 'hours');

  if (updatedDate.isBefore(defaultDate)) return defaultDate;

  return updatedDate.format('YYYY-MM-DDTHH:mm:ss');
};

export const getInitialsChars = (text) => {
  const initialsChars = chain(text)
    .split(' ')
    .map((name) => name[0])
    .slice(0, 2)
    .join('')
    .replace(/[^a-zA-Z0-9\s]/g, '')
    .upperCase()
    .valueOf();

  return initialsChars;
};

export const getOriginWithoutInternalSuffix = () => {
  const originWithoutInternalSuffix = window.location.origin.replace(
    internalSuffix,
    '',
  );

  return originWithoutInternalSuffix;
};

export const hasHtmlTags = (text) => {
  if (!isString(text)) return false;

  return /[<>]/i.test(text);
};
